/**
 * Soft sounds made with Web Audio, no files. "nudge" is a quiet two-note bell
 * for a task that is due, "done" a warm rising chord for a finished task.
 * Muted per browser in localStorage ("oselya.sound" = "off").
 */
type Chime = "nudge" | "done";

const KEY = "oselya.sound";
let context: AudioContext | null = null;

export const soundOn = () => {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
};

export const setSoundOn = (on: boolean) => {
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    // private mode: the switch just does not persist
  }
};

const NOTES: Record<Chime, { freq: number; at: number; length: number }[]> = {
  // E5 then A5, quiet
  nudge: [
    { freq: 659.25, at: 0, length: 0.5 },
    { freq: 880, at: 0.16, length: 0.7 },
  ],
  // C5 E5 G5 C6, an arpeggio that resolves
  done: [
    { freq: 523.25, at: 0, length: 0.45 },
    { freq: 659.25, at: 0.08, length: 0.45 },
    { freq: 783.99, at: 0.16, length: 0.5 },
    { freq: 1046.5, at: 0.26, length: 0.8 },
  ],
};

export const playChime = (kind: Chime) => {
  if (!soundOn() || typeof window === "undefined") return;
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    context ??= new Ctx();
    // browsers start audio only after the person has clicked something
    if (context.state === "suspended") void context.resume();
    const start = context.currentTime + 0.02;
    const volume = kind === "done" ? 0.12 : 0.07;
    for (const note of NOTES[kind]) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = "sine";
      osc.frequency.value = note.freq;
      gain.gain.setValueAtTime(0, start + note.at);
      gain.gain.linearRampToValueAtTime(volume, start + note.at + 0.02);
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        start + note.at + note.length,
      );
      osc.connect(gain).connect(context.destination);
      osc.start(start + note.at);
      osc.stop(start + note.at + note.length + 0.05);
    }
  } catch {
    // no sound is fine
  }
};
